import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, LifeBuoy, CheckCircle2, Clock, AlertCircle, Zap, Send, Search,
  BookOpen, PenSquare, User as UserIcon, ExternalLink, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge, Button, SearchInput, EmptyState } from "@/components/ui-em";

const STATUS_META = {
  new: { label: "New", tone: "warn", icon: AlertCircle },
  in_review: { label: "In Review", tone: "info", icon: Clock },
  answered: { label: "Answered", tone: "success", icon: CheckCircle2 },
  knowledge_gap: { label: "Knowledge Gap", tone: "danger", icon: Zap },
  closed: { label: "Closed", tone: "outline", icon: CheckCircle2 },
};

const STATUS_ORDER = ["new", "in_review", "answered", "knowledge_gap", "closed"];

function StatCard({ label, value, sublabel, icon: Icon, tone = "gold" }) {
  const toneMap = {
    gold: "text-gold border-gold/30 bg-gold/[0.04]",
    emerald: "text-emerald-400 border-emerald-500/30 bg-emerald-500/[0.04]",
    blue: "text-sky-400 border-sky-500/30 bg-sky-500/[0.04]",
    red: "text-red-400 border-red-500/30 bg-red-500/[0.04]",
  };
  return (
    <div className={`em-card p-5 border ${toneMap[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-2">{label}</p>
          <p className="font-display text-3xl font-light text-white">{value}</p>
          {sublabel && <p className="text-[11px] text-zinc-500 mt-2">{sublabel}</p>}
        </div>
        {Icon && <Icon className="w-4 h-4 text-zinc-600" strokeWidth={1.75} />}
      </div>
    </div>
  );
}

export default function AdminEscalations() {
  const { id: routeId } = useParams();
  return routeId ? <EscalationDetail id={routeId} /> : <EscalationList />;
}

function EscalationList() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/escalations/stats"),
        api.get("/admin/escalations", { params: { status: status === "all" ? undefined : status, q: q || undefined } }),
      ]);
      setStats(s.data);
      setItems(l.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [status]);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · EM Support"
        title="AI Escalations"
        description="Answer leaders that need human help. Every escalation is an opportunity to grow the Knowledge Base."
      />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <StatCard label="New" value={stats.counts?.new || 0} icon={AlertCircle} tone="gold" />
          <StatCard label="In Review" value={stats.counts?.in_review || 0} icon={Clock} tone="blue" />
          <StatCard label="Answered" value={stats.counts?.answered || 0} icon={CheckCircle2} tone="emerald" />
          <StatCard label="Knowledge Gaps" value={stats.counts?.knowledge_gap || 0} icon={Zap} tone="red" />
          <StatCard label="Avg Response" value={`${stats.avg_response_hours || 0}h`} sublabel="From creation to answer" tone="gold" />
          <StatCard label="Solved w/o Escalation" value={`${stats.solved_without_escalation_pct || 0}%`} sublabel="AI answers not escalated" tone="emerald" />
        </div>
      )}

      {stats?.top_unanswered?.length > 0 && (
        <Card className="p-6">
          <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-3">Most frequently asked unanswered questions</p>
          <div className="space-y-2">
            {stats.top_unanswered.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-4 text-sm py-1.5">
                <span className="text-zinc-300 truncate">{r.question}</span>
                <Badge variant="outline" size="xs">×{r.count}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="flex-1"><SearchInput value={q} onChange={setQ} placeholder="Search questions, names, emails..." /></div>
        <button
          onClick={load}
          className="px-4 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-zinc-400 hover:text-white"
          data-testid="escalation-search-btn"
          style={{ transitionProperty: "color", transitionDuration: "160ms" }}
        >
          <Search className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {["all", ...STATUS_ORDER].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`filter-status-${s}`}
            className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium ${
              status === s ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10"
            }`}
            style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
          >
            {s === "all" ? "All" : STATUS_META[s]?.label}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={LifeBuoy} title="No escalations yet" description="When leaders click 'No, I still need help' under an EM AI answer, they show up here." className="m-4" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                <th className="px-6 py-4">From</th>
                <th className="px-6 py-4">Question</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const meta = STATUS_META[e.status] || STATUS_META.new;
                const Icon = meta.icon;
                return (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/admin/ai-escalations/${e.id}`)}
                    data-testid={`escalation-row-${e.id}`}
                    className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
                    style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}
                  >
                    <td className="px-6 py-4">
                      <div className="text-white text-sm">{e.name}</div>
                      <div className="text-xs text-zinc-500">{e.email}</div>
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <div className="text-white text-sm truncate">{e.question}</div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" size="xs" uppercase>{e.category}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={meta.tone} size="xs" uppercase icon={Icon}>{meta.label}</Badge>
                    </td>
                    <td className="px-6 py-4 text-xs text-zinc-500">{new Date(e.created_at).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function EscalationDetail({ id }) {
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [answer, setAnswer] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kbArticles, setKbArticles] = useState([]);
  const [showKbUpdate, setShowKbUpdate] = useState(false);
  const [selectedKbArticle, setSelectedKbArticle] = useState("");

  const load = () => api.get(`/admin/escalations/${id}`).then((r) => setE(r.data));
  useEffect(() => {
    load();
    api.get("/kb/articles", { params: { raw: 1 } }).then((r) => setKbArticles(r.data));
  }, [id]);

  const setStatus = async (status) => {
    try {
      await api.put(`/admin/escalations/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitAnswer = async () => {
    if (!answer.trim()) return toast.error("Answer cannot be empty");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/escalations/${id}/answer`, { admin_answer: answer, send_email: sendEmail });
      if (sendEmail) {
        if (data.email_sent) toast.success("Answer sent — user emailed");
        else toast.warning(`Answer saved. Email failed: ${data.email_error || "unknown"}`);
      } else {
        toast.success("Answer saved");
      }
      setAnswer("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const createDraft = async () => {
    if (!e?.admin_answer && !answer.trim()) {
      return toast.error("Answer the question first — the draft will use your answer as its content.");
    }
    try {
      const { data } = await api.post(`/admin/escalations/${id}/kb-action`, {
        action: "create_draft",
        draft_title: e.question,
        draft_category: e.category,
        draft_content: e.admin_answer || answer,
      });
      toast.success("KB draft created");
      load();
      window.open(`/knowledge-base/${data.article_id}/edit`, "_blank");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const linkExisting = async () => {
    if (!selectedKbArticle) return toast.error("Pick an article");
    try {
      await api.post(`/admin/escalations/${id}/kb-action`, {
        action: "update_existing",
        article_id: selectedKbArticle,
        draft_content: e.admin_answer || answer,
      });
      toast.success("Article linked — open it to edit");
      setShowKbUpdate(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this escalation?")) return;
    try {
      await api.delete(`/admin/escalations/${id}`);
      toast.success("Deleted");
      navigate("/admin/ai-escalations");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!e) return <div className="text-zinc-500 text-sm">Loading…</div>;

  const meta = STATUS_META[e.status] || STATUS_META.new;
  const Icon = meta.icon;

  return (
    <div className="space-y-8">
      <button
        onClick={() => navigate("/admin/ai-escalations")}
        className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white"
        style={{ transitionProperty: "color", transitionDuration: "160ms" }}
        data-testid="escalation-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to list
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold mb-2">Escalation · {e.category}</p>
          <h1 className="font-display text-3xl font-light text-white leading-snug">{e.question}</h1>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <Badge variant={meta.tone} size="sm" uppercase icon={Icon}>{meta.label}</Badge>
            <span className="text-xs text-zinc-500">by {e.name} · {e.email}</span>
            <span className="text-xs text-zinc-600">·</span>
            <span className="text-xs text-zinc-500">{new Date(e.created_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_ORDER.filter((s) => s !== e.status).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`set-status-${s}`}
              className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white"
              style={{ transitionProperty: "background-color, color", transitionDuration: "160ms" }}
            >
              Mark as {STATUS_META[s].label}
            </button>
          ))}
          <button onClick={remove} data-testid="escalation-delete" className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-6">
        {/* Left column — context */}
        <div className="space-y-6">
          <Card className="p-6">
            <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-3">EM AI answer</p>
            <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{e.ai_answer || "—"}</div>
          </Card>

          {e.additional_context && (
            <Card className="p-6 border-gold/20 bg-gold/[0.02]">
              <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold mb-3">Additional context from user</p>
              <div className="text-sm text-white leading-relaxed whitespace-pre-wrap">{e.additional_context}</div>
            </Card>
          )}

          {e.conversation_context?.length > 0 && (
            <Card className="p-6">
              <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-3">Conversation history</p>
              <div className="space-y-4">
                {e.conversation_context.map((m, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-white/5 text-zinc-400" : "bg-gold/10 text-gold"}`}>
                      {m.role === "user" ? <UserIcon className="w-3.5 h-3.5" /> : <Zap className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{m.role}</div>
                      <div className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.content}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right column — actions */}
        <div className="space-y-6">
          <Card className="p-6">
            <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold mb-3">Write a verified answer</p>
            {e.admin_answer ? (
              <div className="mb-4 p-4 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20">
                <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Answered by {e.admin_answer_by}</div>
                <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">{e.admin_answer}</div>
                <div className="text-[11px] text-zinc-500 mt-2">{new Date(e.answered_at).toLocaleString()}</div>
              </div>
            ) : null}

            <textarea
              value={answer}
              onChange={(ev) => setAnswer(ev.target.value)}
              placeholder="Write a clear, verified answer for this leader..."
              rows={6}
              className="em-input"
              data-testid="escalation-answer-input"
            />
            <label className="flex items-center gap-2 mt-3 text-xs text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(ev) => setSendEmail(ev.target.checked)}
                className="w-3.5 h-3.5 accent-gold"
                data-testid="escalation-send-email"
              />
              Email this answer to {e.email}
            </label>
            <Button
              onClick={submitAnswer}
              disabled={submitting || !answer.trim()}
              variant="primary"
              size="lg"
              className="w-full mt-4"
              icon={Send}
              data-testid="escalation-answer-submit"
            >
              {e.admin_answer ? "Send new reply" : "Send answer to user"}
            </Button>
          </Card>

          <Card className="p-6">
            <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold mb-3">Knowledge Base learning</p>
            <p className="text-xs text-zinc-500 mb-4">Turn this escalation into KB improvement. Nothing is auto-published — review the draft first.</p>
            <div className="space-y-2">
              <button
                onClick={createDraft}
                data-testid="kb-create-draft"
                className="w-full flex items-center justify-between text-left px-4 py-3 rounded-lg bg-white/5 border border-white/10 hover:border-gold/30 hover:bg-gold/[0.04]"
                style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
              >
                <div className="flex items-center gap-3">
                  <PenSquare className="w-4 h-4 text-gold" />
                  <div>
                    <div className="text-sm text-white">Create new KB draft</div>
                    <div className="text-[11px] text-zinc-500">A new article in draft status — you review before publish</div>
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500" />
              </button>
              <button
                onClick={() => setShowKbUpdate(true)}
                data-testid="kb-update-existing"
                className="w-full flex items-center justify-between text-left px-4 py-3 rounded-lg bg-white/5 border border-white/10 hover:border-gold/30 hover:bg-gold/[0.04]"
                style={{ transitionProperty: "background-color, border-color", transitionDuration: "180ms" }}
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-gold" />
                  <div>
                    <div className="text-sm text-white">Suggest update to existing article</div>
                    <div className="text-[11px] text-zinc-500">Link this escalation to an article you will edit</div>
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500" />
              </button>
            </div>

            {e.kb_related_article_id && (
              <div className="mt-4 p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20 text-xs">
                <div className="text-emerald-400 font-medium mb-1">
                  {e.kb_action === "draft_created" ? "Draft created" : "Update linked"}
                </div>
                <a
                  href={`/knowledge-base/${e.kb_related_article_id}/edit`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-300 hover:text-gold inline-flex items-center gap-1"
                >
                  Open the article to edit <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </Card>

          {showKbUpdate && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-0/70 backdrop-blur-md">
              <div className="em-card p-8 w-full max-w-lg" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
                <h3 className="font-display text-xl mb-4">Link an existing article</h3>
                <select
                  value={selectedKbArticle}
                  onChange={(ev) => setSelectedKbArticle(ev.target.value)}
                  className="em-input mb-4"
                  data-testid="kb-select-article"
                >
                  <option value="">— Select an article —</option>
                  {kbArticles.map((a) => (
                    <option key={a.id} value={a.id}>{a.title} · {a.category}</option>
                  ))}
                </select>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowKbUpdate(false)} className="text-sm px-4 py-2 rounded-lg text-zinc-400 hover:text-white">Cancel</button>
                  <Button onClick={linkExisting} variant="primary" data-testid="kb-link-confirm">Link article</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
